import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle2, XCircle, Lock, Clock, Zap, ChevronDown, RefreshCw, MinusCircle, RotateCcw } from 'lucide-react';
import { getGameProgress } from '../../api/gameProgress';
import './gameProgress.css';

/**
 * Game Progress: the season as a feed, newest round at the top, read downwards to the
 * round the game started on.
 *
 * ---
 *
 * This replaced a strip of W/L pips, one row per player, which fitted a phone and
 * answered the wrong question. The screen exists so somebody can look at what everybody
 * else picked, and a pip says only whether it came off - the team was two taps away
 * behind an expander, one player at a time, so comparing what three people backed in
 * round four meant opening three runs and reading down three lists. The thing people
 * come here for was the thing the layout made hardest.
 *
 * A round is the unit now, because a round is what the conversation is about: who went
 * out on Saturday, who backed the same side as you, who is quietly running out of teams.
 * Every pick in a round is one list, so they are read against each other rather than
 * reconstructed. A season still fits a phone - it is a feed, and scrolling a feed
 * backwards through time is the one gesture nobody has to be taught.
 *
 * Three rules hold it up:
 *
 * A pick is hidden until its round locks, and the hiding is the server's. The old screen
 * fetched everybody's live team and declined to render it, which concealed it from a
 * reader and from nobody at all with a URL bar. A concealed pick is now absent from the
 * response, so there is nothing here that could leak it by accident.
 *
 * Teams remaining are shown on the live round anyway, unlocked, because what somebody has
 * left is the most interesting thing about them before the results start - and it is safe
 * for an arithmetic reason rather than a careful one: the server subtracts only the rounds
 * everybody can already see. A concealed pick cannot move the figure, so the figure cannot
 * name it.
 *
 * And the roster is filterable to one player, because a twenty-round feed is long. That is
 * what the old expandable run was for, and it survives as a filter over the whole history
 * rather than a drawer on one row.
 *
 * It takes a game id and nothing else. Which row is yours comes back marked, because the
 * server resolved it from the session - passing the current user in would be handing the
 * screen a second opinion about who is reading it.
 */
export default function GameProgress({ gameId }) {
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setBoard(await getGameProgress(gameId));
    } catch (err) {
      setError(err.name === 'ApiError' && err.status === 403
        ? 'Only players in this game can see how it is going.'
        : 'Could not load the game progress.');
    } finally {
      setLoading(false);
    }
  }, [gameId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="gp-root gp-centred">
        <RefreshCw size={24} className="gp-spin" aria-hidden="true" />
        <p>Loading the season...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="gp-root gp-centred">
        <p>{error}</p>
        <button type="button" className="gp-retry" onClick={load}>Try again</button>
      </div>
    );
  }

  return <GameProgressBoard board={board} />;
}

/**
 * The board, given the data rather than a game id.
 *
 * Split out from the fetch so the landing page can render a season to somebody who has
 * no game and no session. That page shows the real component with canned props rather
 * than a picture of it, which is the only version that cannot quietly stop matching the
 * product - a screenshot goes stale the first time a pick row is restyled, and nothing
 * fails when it does.
 *
 * The seam is the fetch and only the fetch. Everything a reader can see or do lives
 * here, so the marketing render and the live one are the same code by construction.
 */
export function GameProgressBoard({ board }) {
  const [focusPlayerId, setFocusPlayerId] = useState('all');

  // Taken off the newest round rather than a separate roster call: it is already every
  // player in the game, and a second source could disagree with the rows it is filtering.
  const roster = useMemo(() => {
    const newest = board?.rounds?.[0];
    if (!newest) return [];
    return newest.entries.map(({ playerId, displayName, you }) => ({ playerId, displayName, you }));
  }, [board]);

  if (!board || board.rounds.length === 0) {
    return (
      <div className="gp-root gp-centred">
        <p>Nothing to show yet - this game has not played a round.</p>
      </div>
    );
  }

  const focusId = focusPlayerId === 'all' ? null : Number(focusPlayerId);

  return (
    <div className="gp-root">
      <div className="gp-head">
        <div>
          <h2 className="gp-title">Game Progress</h2>
          <p className="gp-sub">
            {board.remainingCount} of {board.playerCount} still standing
            <span className="gp-dot-sep" aria-hidden="true">·</span>
            {board.rounds.length} {board.rounds.length === 1 ? 'round' : 'rounds'} played
          </p>
        </div>

        {roster.length > 1 && (
          <label className="gp-focus">
            <span className="gp-focus-label">Showing</span>
            <select
              className="gp-focus-select"
              value={focusPlayerId}
              onChange={(e) => setFocusPlayerId(e.target.value)}
            >
              <option value="all">Everyone</option>
              {roster.map((p) => (
                <option key={p.playerId} value={p.playerId}>
                  {p.displayName}{p.you ? ' (you)' : ''}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      <ol className="gp-rounds">
        {board.rounds.map((round) => (
          <Round key={round.gameWeek} round={round} focusId={focusId} />
        ))}
      </ol>
    </div>
  );
}

/* ==================== One round ==================== */

/**
 * A round, with its players grouped by how it went for them.
 *
 * The groups are ordered by what is still undecided: what has not happened yet leads,
 * because on the live round that is the whole card and on a finished one it is empty.
 * Players who were already out are a muted count rather than rows - they were not in
 * this round, and listing them again every round buries the ones who were.
 */
function Round({ round, focusId }) {
  const entries = focusId ? round.entries.filter((e) => e.playerId === focusId) : round.entries;

  const groups = [
    { key: 'pending', title: round.picksRevealed ? 'Awaiting results' : 'Picks', tone: 'wait' },
    { key: 'through', title: 'Through', tone: 'in' },
    { key: 'out', title: 'Out', tone: 'out' },
    { key: 'void', title: 'Void', tone: 'wait' },
  ].map((group) => ({ ...group, rows: entries.filter((e) => bucketOf(e, round) === group.key) }))
    .filter((group) => group.rows.length > 0);

  const alreadyOut = entries.filter((e) => e.outcome === 'NOT_IN_ROUND').length;
  const hasTally = round.survivedCount + round.eliminatedCount + round.pendingCount > 0;

  return (
    <li className={`gp-round${round.current ? ' is-current' : ''}`}>
      <div className="gp-round-head">
        <div className="gp-round-id">
          <h3 className="gp-round-no">Round {round.gameWeek}</h3>
          {round.tournamentMatchWeek != null && (
            <span className="gp-round-mw">Match week {round.tournamentMatchWeek}</span>
          )}
        </div>
        <RoundState round={round} />
      </div>

      {!focusId && hasTally && (
        <p className="gp-tally">
          {round.survivedCount > 0 && <span className="gp-tally-in">{round.survivedCount} through</span>}
          {round.eliminatedCount > 0 && <span className="gp-tally-out">{round.eliminatedCount} out</span>}
          {round.pendingCount > 0 && <span className="gp-tally-wait">{round.pendingCount} to come</span>}
        </p>
      )}

      {/* The server's mark, not a guess from the tally: everybody went out in this round, so
          everybody was put back in for the next. Without it the board showed a round where
          everybody went out followed by one where nobody had, and left the reader to work out why. */}
      {round.rolledOver ? (
        <p className="gp-rollover">
          <RotateCcw size={14} aria-hidden="true" /> Everybody out — rollover, everybody back in
        </p>
      ) : null}

      {groups.map((group) => (
        <section key={group.key} className="gp-group">
          {/* A round where everybody landed in the same place needs no heading to say
              which place - the tally above already said it, and "THROUGH 6" over a list
              of six survivors is the same sentence twice. */}
          {groups.length > 1 && (
            <h4 className={`gp-group-title is-${group.tone}`}>
              {group.title}<span className="gp-group-count">{group.rows.length}</span>
            </h4>
          )}
          <ul className="gp-players">
            {group.rows.map((entry) => (
              <PlayerRow key={entry.playerId} entry={entry} round={round} />
            ))}
          </ul>
        </section>
      ))}

      {groups.length === 0 && (
        <p className="gp-nobody">
          {focusId ? 'Already out by this round.' : 'Nobody was in this round.'}
        </p>
      )}

      {alreadyOut > 0 && !focusId && (
        <p className="gp-already-out">{alreadyOut} already out before this round</p>
      )}
    </li>
  );
}

/**
 * What the round is doing, in the words that matter to somebody who has not picked yet.
 * A locked round says the picks are open rather than that it is locked - "locked" is the
 * state, "everyone's picks are showing" is the news.
 */
function RoundState({ round }) {
  if (round.status === 'VOIDED') {
    return <span className="gp-state is-void">Voided</span>;
  }
  if (round.picksRevealed) {
    return (
      <span className={`gp-state${round.status === 'CLOSED' ? '' : ' is-live'}`}>
        {round.status === 'CLOSED' ? 'Settled' : 'Picks in'}
      </span>
    );
  }
  return (
    <span className="gp-state is-hidden">
      <Lock size={12} aria-hidden="true" />
      {round.pickDeadline ? `Picks hidden until ${formatDeadline(round.pickDeadline)}` : 'Picks hidden'}
    </span>
  );
}

/* ==================== One player in one round ==================== */

function PlayerRow({ entry, round }) {
  const [showTeams, setShowTeams] = useState(false);
  const left = entry.teamsRemaining;

  return (
    <li className={`gp-player${entry.you ? ' is-you' : ''}`}>
      <div className="gp-player-line">
        <OutcomeIcon outcome={entry.outcome} revealed={round.picksRevealed} />
        <span className="gp-player-name">
          {entry.displayName}
          {entry.you && <span className="gp-you">you</span>}
        </span>
        <Pick entry={entry} round={round} />
      </div>

      {left && (
        <div className="gp-left">
          <button
            type="button"
            className="gp-left-btn"
            aria-expanded={showTeams}
            onClick={() => setShowTeams((open) => !open)}
          >
            {left.length} {left.length === 1 ? 'team' : 'teams'} left
            <ChevronDown size={13} className={`gp-caret${showTeams ? ' is-open' : ''}`} aria-hidden="true" />
          </button>
          {showTeams && (
            <ul className="gp-teams">
              {left.map((team) => <li key={team.id} className="gp-team">{team.name}</li>)}
            </ul>
          )}
        </div>
      )}
    </li>
  );
}

/**
 * The pick itself, or the reason there isn't one to show.
 *
 * A concealed pick and no pick at all read differently on purpose: one is a decision made
 * and not yet public, the other is a decision outstanding - and on a round that has locked,
 * the second is what put somebody out.
 */
function Pick({ entry, round }) {
  if (entry.pickConcealed) {
    return (
      <span className="gp-pick is-concealed">
        <Lock size={12} aria-hidden="true" />hidden
      </span>
    );
  }
  if (!entry.teamName) {
    return (
      <span className={`gp-pick${round.picksRevealed ? ' is-missed' : ' is-none'}`}>
        {round.picksRevealed ? 'no pick' : 'yet to pick'}
      </span>
    );
  }
  return (
    <span className="gp-pick">
      {entry.teamName}
      {/* The Gaffer's picks are stored as auto-picks so reopening a week clears them.
          That is plumbing - to everybody else he simply picked. */}
      {entry.autoPick && !entry.bot && (
        <span className="gp-auto" title="Picked automatically"><Zap size={10} aria-hidden="true" />auto</span>
      )}
    </span>
  );
}

function OutcomeIcon({ outcome, revealed }) {
  if (outcome === 'SURVIVED') {
    return <CheckCircle2 size={15} className="gp-icon is-in" aria-label="Through" />;
  }
  if (outcome === 'ELIMINATED' || (outcome === 'NO_PICK' && revealed)) {
    return <XCircle size={15} className="gp-icon is-out" aria-label="Out" />;
  }
  if (outcome === 'VOIDED') {
    return <MinusCircle size={15} className="gp-icon is-none" aria-hidden="true" />;
  }
  return <Clock size={15} className="gp-icon is-wait" aria-label="Waiting" />;
}

/* ==================== Reading the data ==================== */

/**
 * Which group a player's round belongs in.
 *
 * NO_PICK straddles two of them and the round is what decides: outstanding while it is
 * open, and what put them out once it has locked. That is the one place this screen reads
 * an outcome against the round's own status rather than taking it at face value.
 */
function bucketOf(entry, round) {
  switch (entry.outcome) {
    case 'SURVIVED': return 'through';
    case 'ELIMINATED': return 'out';
    case 'VOIDED': return 'void';
    case 'NO_PICK': return round.picksRevealed ? 'out' : 'pending';
    case 'PENDING': return 'pending';
    default: return 'none';
  }
}

/** Short enough to sit on one line of a phone: "Sat 15:00". */
function formatDeadline(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.toLocaleDateString('en-GB', { weekday: 'short' })} ${date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
}
